(() => {
  const stateStore = (window.CartDrawerState = window.CartDrawerState || new Map());
  const instanceStore = (window.CartDrawerInstances = window.CartDrawerInstances || new Map());

  // Debounce utility to prevent rapid-fire cart updates
  const createDebouncer = () => {
    let pending = null;
    let isProcessing = false;

    return async (fn) => {
      if (isProcessing) {
        pending = fn;
        return;
      }
      isProcessing = true;
      try {
        await fn();
      } finally {
        isProcessing = false;
        if (pending) {
          const next = pending;
          pending = null;
          await next();
        }
      }
    };
  };

  // Fetch with timeout wrapper
  const fetchWithTimeout = (url, options = {}, timeout = 10000) => {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), timeout);

    return fetch(url, { ...options, signal: controller.signal })
      .finally(() => clearTimeout(timeoutId));
  };

  const getRootUrl = () => {
    if (window.Shopify && Shopify.routes && Shopify.routes.root) {
      return Shopify.routes.root;
    }
    return "/";
  };

  const getSectionId = (root) => root && root.dataset && root.dataset.sectionId;

  const getDefaultInstance = () => {
    const first = document.querySelector("[data-cart-drawer]");
    if (!first) return null;
    return instanceStore.get(getSectionId(first));
  };

  const openInstance = (sectionId) => {
    if (sectionId && instanceStore.has(sectionId)) {
      instanceStore.get(sectionId).open();
      return;
    }
    const fallback = getDefaultInstance();
    if (fallback) fallback.open();
  };

  const closeInstance = (sectionId) => {
    if (sectionId && instanceStore.has(sectionId)) {
      instanceStore.get(sectionId).close();
      return;
    }
    const fallback = getDefaultInstance();
    if (fallback) fallback.close();
  };

  const bindGlobal = () => {
    if (window.__cartDrawerGlobalBound) return;
    window.__cartDrawerGlobalBound = true;

    document.addEventListener("click", (event) => {
      const trigger = event.target.closest(
        "[data-cart-drawer-open], a[href^=\"#cart-drawer-\"]"
      );
      if (trigger) {
        event.preventDefault();
        const targetId =
          trigger.getAttribute("data-cart-drawer-open") ||
          (trigger.getAttribute("href") || "").replace("#", "");
        if (targetId) {
          openInstance(targetId.replace("cart-drawer-", ""));
        } else {
          openInstance();
        }
        return;
      }

    });

    ["cart:open", "cart-drawer:open", "ajax-cart:open", "cart:toggle", "open-cart", "sidebar:open"].forEach(
      (evt) => {
        document.addEventListener(evt, () => {
          openInstance();
        });
      }
    );

    document.addEventListener("cart:close", () => {
      closeInstance();
    });
  };

  const fetchSectionHtml = async (sectionId) => {
    const url = `${getRootUrl()}?section_id=${sectionId}`;
    const response = await fetch(url, { credentials: "same-origin" });
    return response.text();
  };

  const refreshSection = async (sectionId) => {
    const currentSection = document.getElementById(`shopify-section-${sectionId}`);
    if (!currentSection) return;

    const html = await fetchSectionHtml(sectionId);
    const parsed = new DOMParser().parseFromString(html, "text/html");
    const nextSection = parsed.getElementById(`shopify-section-${sectionId}`);
    if (!nextSection) return;

    currentSection.innerHTML = nextSection.innerHTML;
    const newRoot = currentSection.querySelector("[data-cart-drawer]");
    if (newRoot) {
      initCartDrawer(newRoot);
    }
  };

  const initCartDrawer = (root) => {
    if (!root) return;

    const sectionId = getSectionId(root);
    if (!sectionId) return;

    const getStoredDiscount = () => {
      try {
        const value = window.localStorage.getItem("cartDiscountCode");
        return value ? value.trim() : "";
      } catch (e) {
        return "";
      }
    };

    const isValidDiscountCode = (code) => {
      if (!code || typeof code !== "string") return false;
      // Allow alphanumeric, hyphens, underscores only (max 50 chars)
      const pattern = /^[a-zA-Z0-9_-]{1,50}$/;
      return pattern.test(code);
    };

    const setStoredDiscount = (value) => {
      try {
        if (value && isValidDiscountCode(value)) {
          window.localStorage.setItem("cartDiscountCode", value);
        } else {
          window.localStorage.removeItem("cartDiscountCode");
        }
      } catch (e) {}
    };

    const getCheckoutUrl = () => {
      const code = getStoredDiscount();
      if (!code) return "/checkout";
      return `/checkout?discount=${encodeURIComponent(code)}`;
    };

    const existingState = stateStore.get(sectionId);
    const state = existingState || {
      isOpen: false,
      inlineUpsellHidden: false,
      hasDismissedUpsell: false,
      showUpsellSheet: false,
      upsellTimer: null,
      scrollY: 0,
    };
    stateStore.set(sectionId, state);

    if (instanceStore.has(sectionId)) {
      const instance = instanceStore.get(sectionId);
      instance.root = root;
      instance.bind();
      instance.applyState();
      return;
    }

    const instance = {
      root,
      sectionId,
      state,
      open: () => {
        instance.state.isOpen = true;
        instance.state.scrollY = window.scrollY || window.pageYOffset || 0;
        document.documentElement.classList.add("cart-drawer-open");
        document.body.classList.add("cart-drawer-open");
        document.body.style.top = `-${instance.state.scrollY}px`;
        instance.root.classList.add("is-open");
        instance.applyState();
        instance.scheduleUpsell();
      },
      close: () => {
        instance.root.classList.remove("is-open");
        instance.state.isOpen = false;
        instance.state.showUpsellSheet = false;
        instance.state.hasDismissedUpsell = false;
        instance.clearUpsellTimer();
        document.documentElement.classList.remove("cart-drawer-open");
        document.body.classList.remove("cart-drawer-open");
        document.body.style.top = "";
        if (typeof instance.state.scrollY === "number") {
          window.scrollTo(0, instance.state.scrollY);
        }
        instance.applyState();
      },
      clearUpsellTimer: () => {
        if (instance.state.upsellTimer) {
          clearTimeout(instance.state.upsellTimer);
          instance.state.upsellTimer = null;
        }
      },
      scheduleUpsell: () => {
        const sheet = instance.root.querySelector("[data-upsell-sheet]");
        if (!sheet) return;
        const eligible = sheet.dataset.eligible === "true";
        if (!instance.state.isOpen) return;
        if (!eligible || instance.state.hasDismissedUpsell) {
          instance.hideUpsell();
          return;
        }
        if (instance.state.showUpsellSheet) return;
        instance.clearUpsellTimer();
        // Show upsell immediately
        instance.showUpsell();
      },
      triggerUpsellOnScroll: () => {
        const scrollArea = instance.root.querySelector(".cd-scroll");
        if (!scrollArea) return;
        const sheet = instance.root.querySelector("[data-upsell-sheet]");
        if (!sheet) return;
        const eligible = sheet.dataset.eligible === "true";
        if (!eligible || instance.state.hasDismissedUpsell || instance.state.showUpsellSheet) return;

        const scrollBottom = scrollArea.scrollTop + scrollArea.clientHeight;
        const scrollHeight = scrollArea.scrollHeight;
        // Trigger when user scrolls past 60% of content
        if (scrollBottom > scrollHeight * 0.6) {
          instance.showUpsell();
        }
      },
      showUpsell: () => {
        const sheet = instance.root.querySelector("[data-upsell-sheet]");
        if (!sheet) return;
        instance.state.showUpsellSheet = true;
        sheet.classList.add("is-visible");
      },
      hideUpsell: () => {
        const sheet = instance.root.querySelector("[data-upsell-sheet]");
        if (!sheet) return;
        instance.state.showUpsellSheet = false;
        sheet.classList.remove("is-visible");
      },
      applyDiscountState: () => {
        const wrapper = instance.root.querySelector("[data-discount]");
        if (!wrapper) return;
        const label = wrapper.querySelector("[data-discount-label]");
        const input = wrapper.querySelector("[data-discount-input]");
        const hint = wrapper.querySelector("[data-discount-hint]");
        const code = getStoredDiscount();
        if (label) {
          label.textContent = code ? `Discount code: ${code}` : "Have a discount code?";
        }
        if (input) {
          input.value = code || "";
        }
        if (hint) {
          hint.textContent = "Applied at checkout.";
        }
        wrapper.classList.toggle("has-code", !!code);
      },
      applyState: () => {
        if (instance.state.isOpen) {
          instance.root.classList.add("is-open");
        } else {
          instance.root.classList.remove("is-open");
        }

        const inlineUpsell = instance.root.querySelector("[data-inline-upsell]");
        if (inlineUpsell) {
          const eligible = inlineUpsell.dataset.inlineEligible === "true";
          const shouldHide = instance.state.inlineUpsellHidden || !eligible;
          inlineUpsell.classList.toggle("is-hidden", shouldHide);
        }

        const sheet = instance.root.querySelector("[data-upsell-sheet]");
        if (sheet) {
          sheet.classList.toggle("is-visible", instance.state.showUpsellSheet);
        }

        instance.applyDiscountState();
      },
      bind: () => {
        if (instance.root.dataset.cartDrawerInit === "true") return;
        instance.root.dataset.cartDrawerInit = "true";

        instance.root.addEventListener("click", (event) => {
          if (!event.target.closest(".cd-panel")) {
            instance.close();
            return;
          }

          const closeBtn = event.target.closest("[data-cart-drawer-close]");
          if (closeBtn) {
            event.preventDefault();
            instance.close();
            return;
          }

          const removeBtn = event.target.closest("[data-cart-remove]");
          if (removeBtn) {
            event.preventDefault();
            const line = parseInt(removeBtn.dataset.line || "0", 10);
            if (line) {
              instance.updateLine(line, 0);
            }
            return;
          }

          const minusBtn = event.target.closest("[data-qty-minus]");
          if (minusBtn) {
            event.preventDefault();
            const line = parseInt(minusBtn.dataset.line || "0", 10);
            const item = minusBtn.closest("[data-cart-item]");
            const qtyEl = item && item.querySelector("[data-qty-value]");
            const current = qtyEl ? parseInt(qtyEl.textContent || "0", 10) : 0;
            if (line) {
              if (current > 1) {
                instance.updateLine(line, current - 1);
              } else {
                instance.updateLine(line, 0);
              }
            }
            return;
          }

          const plusBtn = event.target.closest("[data-qty-plus]");
          if (plusBtn) {
            event.preventDefault();
            const line = parseInt(plusBtn.dataset.line || "0", 10);
            const item = plusBtn.closest("[data-cart-item]");
            const qtyEl = item && item.querySelector("[data-qty-value]");
            const current = qtyEl ? parseInt(qtyEl.textContent || "0", 10) : 0;
            if (line) {
              instance.updateLine(line, current + 1);
            }
            return;
          }

          const clearBtn = event.target.closest("[data-clear-cart]");
          if (clearBtn) {
            event.preventDefault();
            instance.clearCart();
            return;
          }

          const inlineDismiss = event.target.closest("[data-inline-upsell-dismiss]");
          if (inlineDismiss) {
            event.preventDefault();
            instance.state.inlineUpsellHidden = true;
            instance.applyState();
            return;
          }

          const upsellDismiss = event.target.closest("[data-upsell-dismiss]");
          if (upsellDismiss) {
            event.preventDefault();
            instance.state.hasDismissedUpsell = true;
            instance.hideUpsell();
            return;
          }

          const discountToggle = event.target.closest("[data-discount-toggle]");
          if (discountToggle) {
            event.preventDefault();
            const wrapper = discountToggle.closest("[data-discount]");
            if (wrapper) {
              const isOpen = wrapper.classList.toggle("is-open");
              discountToggle.setAttribute("aria-expanded", isOpen ? "true" : "false");
            }
            return;
          }

          const discountApply = event.target.closest("[data-discount-apply]");
          if (discountApply) {
            event.preventDefault();
            const wrapper = discountApply.closest("[data-discount]");
            if (wrapper) {
              const input = wrapper.querySelector("[data-discount-input]");
              const value = input && input.value ? input.value.trim() : "";
              if (value) {
                setStoredDiscount(value);
                instance.applyDiscountState();
              }
            }
            return;
          }

          const discountClear = event.target.closest("[data-discount-clear]");
          if (discountClear) {
            event.preventDefault();
            setStoredDiscount("");
            instance.applyDiscountState();
            return;
          }

          const checkoutBtn = event.target.closest("[data-cart-checkout]");
          if (checkoutBtn) {
            event.preventDefault();
            window.location.href = getCheckoutUrl();
          }
        });

        instance.root.addEventListener("keydown", (event) => {
          const input = event.target.closest("[data-discount-input]");
          if (!input) return;
          if (event.key === "Enter") {
            event.preventDefault();
            const value = input.value ? input.value.trim() : "";
            if (value) {
              setStoredDiscount(value);
              instance.applyDiscountState();
            }
          }
        });

        instance.root.addEventListener("submit", (event) => {
          const form = event.target.closest("[data-upsell-form]");
          if (!form) return;
          event.preventDefault();
          instance.addUpsell(form);
        });

        instance.root.addEventListener("change", (event) => {
          const select = event.target.closest("[data-variant-select]");
          if (!select) return;
          const form = select.closest("[data-upsell-form]");
          if (!form) return;
          const flavorInput = form.querySelector("[data-flavor-input]");
          if (flavorInput) {
            const option = select.options[select.selectedIndex];
            flavorInput.value = option ? option.textContent.trim() : "";
          }
        });

        const footer = instance.root.querySelector("[data-cart-footer]");
        if (footer) {
          const triggerUpsell = () => {
            const sheet = instance.root.querySelector("[data-upsell-sheet]");
            const eligible = sheet && sheet.dataset.eligible === "true";
            if (eligible && !instance.state.hasDismissedUpsell && !instance.state.showUpsellSheet) {
              instance.showUpsell();
            }
          };
          footer.addEventListener("mouseenter", triggerUpsell);
          footer.addEventListener("touchstart", triggerUpsell, { passive: true });
        }

        // Scroll-triggered upsell
        const scrollArea = instance.root.querySelector(".cd-scroll");
        if (scrollArea) {
          scrollArea.addEventListener("scroll", () => {
            instance.triggerUpsellOnScroll();
          }, { passive: true });
        }

        // Swipe-to-remove touch handlers
        instance.root.querySelectorAll("[data-swipe-item]").forEach((swipeItem) => {
          const card = swipeItem.querySelector("[data-swipe-card]");
          if (!card) return;

          let startX = 0;
          let startY = 0;
          let offsetX = 0;
          let isDragging = false;
          let hasHaptic = false;
          const MAX_SWIPE = -140;
          const ARM_THRESHOLD = -110;

          const handleTouchStart = (e) => {
            if (e.target.closest("[data-quantity-wrapper]")) return;
            startX = e.touches[0].clientX;
            startY = e.touches[0].clientY;
            offsetX = 0;
            hasHaptic = false;
            isDragging = true;
            swipeItem.classList.add("is-swiping");
            swipeItem.classList.remove("is-armed");
            card.classList.add("is-swiping");
            card.classList.remove("is-snapping");
          };

          const handleTouchMove = (e) => {
            if (!isDragging) return;
            const currentX = e.touches[0].clientX;
            const currentY = e.touches[0].clientY;
            const diffX = currentX - startX;
            const diffY = currentY - startY;

            if (Math.abs(diffY) > Math.abs(diffX)) return;

            const clamped = Math.min(0, Math.max(diffX, MAX_SWIPE));
            if (clamped === 0) {
              offsetX = 0;
              swipeItem.classList.remove("is-armed");
              card.style.transform = "translateX(0)";
              return;
            }
            if (e.cancelable) e.preventDefault();
            offsetX = clamped;
            card.style.transform = `translateX(${offsetX}px)`;
            const armed = offsetX <= ARM_THRESHOLD;
            swipeItem.classList.toggle("is-armed", armed);
            if (armed && !hasHaptic && "vibrate" in navigator) {
              navigator.vibrate(10);
              hasHaptic = true;
            }
          };

          const handleTouchEnd = () => {
            if (!isDragging) return;
            isDragging = false;
            swipeItem.classList.remove("is-swiping");
            card.classList.remove("is-swiping");
            card.classList.add("is-snapping");

            if (offsetX <= ARM_THRESHOLD) {
              card.style.transform = "translateX(-500px)";
              const line = parseInt(swipeItem.dataset.line || "0", 10);
              setTimeout(() => {
                if (line) instance.updateLine(line, 0);
              }, 300);
            } else {
              card.style.transform = "translateX(0)";
              swipeItem.classList.remove("is-armed");
            }
            offsetX = 0;
          };

          swipeItem.addEventListener("touchstart", handleTouchStart, { passive: true });
          swipeItem.addEventListener("touchmove", handleTouchMove, { passive: false });
          swipeItem.addEventListener("touchend", handleTouchEnd);
          swipeItem.addEventListener("touchcancel", handleTouchEnd);
        });
      },
      updateLine: (line, quantity) => {
        instance.cartDebouncer(async () => {
          try {
            await fetchWithTimeout("/cart/change.js", {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
                Accept: "application/json",
              },
              body: JSON.stringify({ line, quantity }),
              credentials: "same-origin",
            });

            await refreshSection(instance.sectionId);
            const next = instanceStore.get(instance.sectionId);
            if (next) {
              next.state.isOpen = true;
              next.applyState();
              next.scheduleUpsell();
            }
          } catch (e) {
            if (e.name === "AbortError") {
              console.warn("Cart update timed out");
            }
          }
        });
      },
      clearCart: () => {
        instance.cartDebouncer(async () => {
          try {
            await fetchWithTimeout("/cart/clear.js", {
              method: "POST",
              headers: { Accept: "application/json" },
              credentials: "same-origin",
            });
            await refreshSection(instance.sectionId);
            const next = instanceStore.get(instance.sectionId);
            if (next) {
              next.state.isOpen = true;
              next.applyState();
              next.scheduleUpsell();
            }
          } catch (e) {
            if (e.name === "AbortError") {
              console.warn("Cart clear timed out");
            }
          }
        });
      },
      addUpsell: (form) => {
        const formData = new FormData(form);
        instance.cartDebouncer(async () => {
          try {
            await fetchWithTimeout("/cart/add.js", {
              method: "POST",
              headers: { Accept: "application/json" },
              body: formData,
              credentials: "same-origin",
            });
            await refreshSection(instance.sectionId);
            const next = instanceStore.get(instance.sectionId);
            if (next) {
              next.state.isOpen = true;
              next.applyState();
              next.scheduleUpsell();
            }
          } catch (e) {
            if (e.name === "AbortError") {
              console.warn("Upsell add timed out");
            }
          }
        });
      },
    };

    // Create debouncer for this instance
    instance.cartDebouncer = createDebouncer();

    instanceStore.set(sectionId, instance);
    instance.bind();
    instance.applyState();
  };

  const initAll = () => {
    document.querySelectorAll("[data-cart-drawer]").forEach((root) => {
      initCartDrawer(root);
    });
  };

  bindGlobal();
  document.addEventListener("cart:refresh", () => {
    const instance = getDefaultInstance();
    if (instance) {
      refreshSection(instance.sectionId);
    }
  });
  document.addEventListener("DOMContentLoaded", initAll);
  document.addEventListener("shopify:section:load", (event) => {
    const section = event.target;
    if (!section) return;
    const root = section.querySelector("[data-cart-drawer]");
    if (root) {
      initCartDrawer(root);
    }
  });
})();
